import React, { useRef, useState, FC } from 'react';
import { isImeComposing } from './isImeComposing';
import { tabId } from './TableScroll';

interface TabProps {
  tabs: { label: string, value: any }[];
  onTabClick: (value: any) => void;
}

/**
 * ADM-16-AC2。タブの並び（tablist）と選択中のタブ（aria-selected）を読み上げで分かるようにし、
 * 左右の矢印キー・Home・End で隣のタブへ移って選ぶ（Tab キーでは選択中のタブだけに止まる）。
 */
const Tab: FC<TabProps> = ({ tabs, onTabClick }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    onTabClick(tabs[index].value);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (isImeComposing(event)) return; // EV-120
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
        ? (index - 1 + tabs.length) % tabs.length
        : event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? last
        : null;
    if (next === null) return;
    event.preventDefault();
    tabRefs.current[next]?.focus();
    handleTabClick(next);
  };

  return (
    <div>
      {/* EV-309。1024px 未満で並びきらないとき（768px の英語の運転者の詳細など）は、タブの並びの中だけを横に送る（ページを
          送らない）。1024px 未満ではタブの文字を折らない（折ると「アルコ／ール報／告」のように語の途中で折れる）。
          横送りは 1024px 未満だけ（#248 の独立照合の指摘）。overflow-x: auto は overflow-y も auto にし、並びの上端に接する
          タブのフォーカスの枠（外側に描く）の上・左・下を切るので、1024px 以上は今までどおり overflow: visible にする。
          1024px 未満では、タブのフォーカスの枠を内側に描いて（-outline-offset-2）切られないようにする。
          EV-310。button の id は、表の外枠の読み上げの名前に使う。 */}
      <div className="flex border-b-2 mb-4 max-lg:overflow-x-auto" role="tablist">
        {tabs.map((tab, index) => (
          <button
            key={index}
            id={tabId(String(tab.value))}
            type="button"
            role="tab"
            aria-selected={activeTab === index}
            tabIndex={activeTab === index ? 0 : -1}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            className={`px-6 text-sm font-medium pb-3 pt-1 max-lg:whitespace-nowrap max-lg:focus-visible:-outline-offset-2 ${activeTab === index ? 'border-b-2 border-primary/300 text-primary/300 text-body-1' : 'text-gray-500'}`}
            onClick={() => handleTabClick(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Tab;
